import { useState, useEffect } from "react";
import { Loader2, KeyRound, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function SecuritySettings() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [account, setAccount] = useState({ name: "", email: "" });
  const [accSaving, setAccSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => { if (user) setAccount({ name: user.name || "", email: user.email || "" }); }, [user]);

  const saveAccount = async (e) => {
    e.preventDefault();
    setAccSaving(true);
    try {
      await api.post("/auth/update-profile", account);
      await checkAuth();
      toast.success("تم تحديث بيانات الحساب");
    } catch (err) { toast.error(apiErr(err)); } finally { setAccSaving(false); }
  };

  useEffect(() => { api.get("/admin/activity-log").then((r) => setLogs(r.data)).catch(() => {}); }, []);

  const changePw = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("كلمتا المرور غير متطابقتين"); return; }
    if (form.new_password.length < 6) { toast.error("كلمة المرور قصيرة جداً"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("تم تغيير كلمة المرور"); setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">الأمان والحساب</h1>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5">
        <h2 className="font-bold flex items-center gap-2 mb-4"><UserCog className="h-5 w-5" /> بيانات الحساب</h2>
        <form onSubmit={saveAccount} className="grid sm:grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs">الاسم</Label><Input value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} className="rounded-xl h-11" data-testid="account-name" /></div>
          <div><Label className="mb-1 block text-xs">البريد الإلكتروني (لتسجيل الدخول)</Label><Input type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} className="rounded-xl h-11" dir="ltr" data-testid="account-email" /></div>
          <div className="sm:col-span-2"><Button type="submit" disabled={accSaving} className="rounded-full font-semibold" data-testid="save-account-btn">{accSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ بيانات الحساب"}</Button></div>
        </form>
      </section>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5">
        <h2 className="font-bold flex items-center gap-2 mb-4"><KeyRound className="h-5 w-5" /> تغيير كلمة المرور</h2>
        <form onSubmit={changePw} className="space-y-3">
          <div><Label className="mb-1 block text-xs">كلمة المرور الحالية</Label><Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="rounded-xl h-11" dir="ltr" data-testid="current-password" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">كلمة المرور الجديدة</Label><Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="rounded-xl h-11" dir="ltr" data-testid="new-password" /></div>
            <div><Label className="mb-1 block text-xs">تأكيد كلمة المرور</Label><Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="rounded-xl h-11" dir="ltr" data-testid="confirm-password" /></div>
          </div>
          <Button type="submit" disabled={saving} className="rounded-full font-semibold" data-testid="change-password-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "تحديث كلمة المرور"}</Button>
        </form>
      </section>

      <section className="rounded-2xl bg-card cb-card-shadow p-5">
        <h2 className="font-bold mb-4">سجل النشاطات</h2>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {logs.length === 0 && <p className="text-sm text-muted-foreground">لا يوجد سجل بعد</p>}
          {logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-border/50">
              <div><span className="font-medium">{l.action}</span> <span className="text-muted-foreground text-xs">{l.detail}</span></div>
              <span className="text-xs text-muted-foreground" dir="ltr">{new Date(l.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
